import React from 'react';
import ReactDOM from 'react-dom';
import { LinearProgress, ThemeProvider } from '@mui/material';
import mainTheme from '../assets/styles/themes/mainTheme';
import { RequestsProgress } from '@klab-berlin/api-sdk/lib/types/config';
import { zIndex } from '../constants';

const PROGRESS_CONTAINER_ID = 'mui-linear-progress';
const HIDE_DELAY_MS = 150;
const MAX_AUTO_PROGRESS = 90;
const AUTO_INCREMENT_STEP = 0.6;

let container: HTMLDivElement | null = null;
let hideTimeout: ReturnType<typeof setTimeout> | null = null;
let animationFrame: number | null = null;
let isVisible = false;
let progressValue = 0;

const ensureContainer = () => {
  if (typeof document === 'undefined') return null;

  if (!container) {
    container = document.createElement('div');
    container.id = PROGRESS_CONTAINER_ID;
    Object.assign(container.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      right: '0',
      zIndex: zIndex.muiProgress,
      pointerEvents: 'none',
    });
  }

  if (!container.parentElement) {
    document.body.appendChild(container);
  }

  return container;
};

const stopAutoIncrement = () => {
  if (animationFrame !== null) {
    window.cancelAnimationFrame(animationFrame);
    animationFrame = null;
  }
};

const clearHideTimeout = () => {
  if (!hideTimeout) return;
  window.clearTimeout(hideTimeout);
  hideTimeout = null;
};

const renderProgress = () => {
  const target = ensureContainer();
  if (!target) return;

  ReactDOM.render(
    <ThemeProvider theme={mainTheme}>
      <LinearProgress
        color='primary'
        variant='determinate'
        value={progressValue}
        sx={{ height: 4 }}
      />
    </ThemeProvider>,
    target
  );
  isVisible = true;
};

const autoIncrement = () => {
  if (progressValue >= MAX_AUTO_PROGRESS) {
    animationFrame = null;
    return;
  }

  progressValue = Math.min(progressValue + AUTO_INCREMENT_STEP, MAX_AUTO_PROGRESS);
  renderProgress();
  animationFrame = window.requestAnimationFrame(autoIncrement);
};

const hideProgress = () => {
  clearHideTimeout();
  
  if (!container) return;

  hideTimeout = window.setTimeout(() => {
    if (hideTimeout && container) {
      ReactDOM.unmountComponentAtNode(container);
      container.remove();
      container = null;
    }
    isVisible = false;
    progressValue = 0;
    hideTimeout = null;
  }, HIDE_DELAY_MS) as unknown as ReturnType<typeof setTimeout>;
};

const muiProgress: RequestsProgress = {
  inc: () => {
    clearHideTimeout();
  
    if (!isVisible || progressValue === 0 || progressValue >= 100) {
      stopAutoIncrement();
      progressValue = progressValue >= 100 ? 0 : progressValue;
      renderProgress();
      animationFrame = window.requestAnimationFrame(autoIncrement);
    }
  },
  done: () => {
    clearHideTimeout();
    stopAutoIncrement();
    if (!isVisible) return;

    progressValue = 100;
    renderProgress();
    hideProgress();
  },
};

export default muiProgress;
